/**
 * EmailSettingsPanel — E-Mail-Einstellungen
 * 
 * Verwaltung von:
 * - E-Mail-Konten (Status, Verbindung testen)
 * - Signaturen pro Konto
 * - Standard-Konto setzen
 * - Auto-Sync Einstellungen
 */

import { useState, useEffect } from 'react';
import {
  X, Mail, Settings, Check, AlertCircle, Loader2, Save,
  RefreshCw, Wifi, WifiOff, Pen, Trash2, Plus, Star,
} from 'lucide-react';
import { toast } from 'sonner';
import type { EmailAccount, EmailSignature } from '@/hooks/useEmailAccounts';

interface Props {
  open: boolean;
  onClose: () => void;
  accounts: EmailAccount[];
  onUpdateAccount: (accountId: string, updates: Partial<EmailAccount>) => Promise<any>;
  onTestConnection: (accountId: string) => Promise<any>;
  signatures: EmailSignature[];
  onSaveSignature: (signature: EmailSignature) => Promise<any>;
  onReloadAccounts: () => void;
}

export default function EmailSettingsPanel({
  open,
  onClose,
  accounts,
  onUpdateAccount,
  onTestConnection,
  signatures,
  onSaveSignature,
  onReloadAccounts,
}: Props) {
  const [activeTab, setActiveTab] = useState<'konten' | 'signaturen'>('konten');
  const [testing, setTesting] = useState<string | null>(null);
  const [editingSignature, setEditingSignature] = useState<EmailSignature | null>(null);
  const [saving, setSaving] = useState(false);

  if (!open) return null;

  const handleTestConnection = async (accountId: string) => {
    setTesting(accountId);
    try {
      const result = await onTestConnection(accountId);
      if (result?.success) {
        toast.success('Verbindung erfolgreich');
      } else {
        toast.error(result?.error || 'Verbindung fehlgeschlagen');
      }
    } catch (err: any) {
      toast.error(err.message || 'Verbindungstest fehlgeschlagen');
    }
    setTesting(null);
  };

  const handleSetDefault = async (accountId: string) => {
    try {
      await onUpdateAccount(accountId, { is_default: true } as any);
      toast.success('Standard-Konto gesetzt');
      onReloadAccounts();
    } catch (err: any) {
      toast.error(err.message);
    }
  };

  const handleSaveSignature = async () => {
    if (!editingSignature) return;
    if (!editingSignature.name.trim()) {
      toast.error('Bitte einen Namen eingeben');
      return;
    }
    setSaving(true);
    try {
      await onSaveSignature(editingSignature);
      toast.success('Signatur gespeichert');
      setEditingSignature(null);
    } catch (err: any) {
      toast.error(err.message);
    }
    setSaving(false);
  };

  const tabs = [
    { id: 'konten' as const, label: 'E-Mail-Konten', icon: Mail },
    { id: 'signaturen' as const, label: 'Signaturen', icon: Pen },
  ];

  return (
    <div className="fixed inset-0 z-[110] flex items-center justify-center">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-[2px]" onClick={onClose} />
      <div className="relative bg-white w-full max-w-3xl rounded-2xl max-h-[85vh] flex flex-col shadow-2xl mx-4">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-gray-100 flex items-center justify-center">
              <Settings className="w-5 h-5 text-gray-600" />
            </div>
            <h3 className="text-base font-bold text-gray-900">E-Mail-Einstellungen</h3>
          </div>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100">
            <X className="w-4 h-4 text-gray-500" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-gray-200 flex-shrink-0">
          {tabs.map(tab => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-5 py-3 text-sm font-semibold border-b-2 transition-colors ${
                  activeTab === tab.id
                    ? 'border-[#C41E3A] text-[#C41E3A]'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                <Icon className="w-4 h-4" />
                {tab.label}
              </button>
            );
          })}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-5">
          {activeTab === 'konten' && (
            <div className="space-y-4">
              {accounts.map(account => (
                <div key={account.id} className="border border-gray-200 rounded-xl p-4 hover:border-gray-300 transition-colors">
                  <div className="flex items-start gap-4">
                    <div
                      className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0"
                      style={{ backgroundColor: account.color + '15' }}
                    >
                      <Mail className="w-6 h-6" style={{ color: account.color }} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <h4 className="text-sm font-bold text-gray-900">{account.name}</h4>
                        {account.is_default && (
                          <span className="text-[10px] bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full font-bold">Standard</span>
                        )}
                        <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold ${
                          account.is_active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                        }`}>
                          {account.is_active ? 'Aktiv' : 'Inaktiv'}
                        </span>
                      </div>
                      <p className="text-sm text-gray-600">{account.email}</p>
                      <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
                        <span className="flex items-center gap-1">
                          <span className="font-medium">Provider:</span> {account.provider.toUpperCase()}
                        </span>
                        {account.last_sync_at && (
                          <span className="flex items-center gap-1">
                            <RefreshCw className="w-3 h-3" />
                            Letzter Sync: {new Date(account.last_sync_at).toLocaleString('de-DE')}
                          </span>
                        )}
                      </div>
                      {account.last_sync_error && (
                        <div className="flex items-center gap-1.5 mt-2 text-xs text-amber-600 bg-amber-50 px-2 py-1 rounded-lg">
                          <AlertCircle className="w-3 h-3 flex-shrink-0" />
                          {account.last_sync_error}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 mt-4 pt-3 border-t border-gray-100">
                    <button
                      onClick={() => handleTestConnection(account.id)}
                      disabled={testing === account.id}
                      className="flex items-center gap-1.5 text-xs font-medium text-gray-600 hover:text-gray-800 px-3 py-2 rounded-lg hover:bg-gray-100 transition-colors"
                    >
                      {testing === account.id ? (
                        <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      ) : (
                        <Wifi className="w-3.5 h-3.5" />
                      )}
                      Verbindung testen
                    </button>
                    {!account.is_default && (
                      <button
                        onClick={() => handleSetDefault(account.id)}
                        className="flex items-center gap-1.5 text-xs font-medium text-blue-600 hover:text-blue-700 px-3 py-2 rounded-lg hover:bg-blue-50 transition-colors"
                      >
                        <Star className="w-3.5 h-3.5" />
                        Als Standard setzen
                      </button>
                    )}
                  </div>
                </div>
              ))}

              {accounts.length === 0 && (
                <div className="text-center py-8">
                  <Mail className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                  <p className="text-sm text-gray-500">Keine E-Mail-Konten konfiguriert</p>
                </div>
              )}
            </div>
          )}

          {activeTab === 'signaturen' && (
            <div className="space-y-4">
              {editingSignature ? (
                <div className="space-y-4">
                  <button onClick={() => setEditingSignature(null)} className="text-sm text-blue-600 hover:underline font-medium">
                    ← Zurück zur Liste
                  </button>
                  <div>
                    <label className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5 block">Signaturname</label>
                    <input
                      value={editingSignature.name}
                      onChange={e => setEditingSignature({ ...editingSignature, name: e.target.value })}
                      className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2.5 focus:border-blue-400 outline-none"
                      placeholder="z.B. Standard, Formal, MietGo"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5 block">Konto</label>
                    <select
                      value={editingSignature.account_id}
                      onChange={e => setEditingSignature({ ...editingSignature, account_id: e.target.value })}
                      className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2.5 focus:border-blue-400 outline-none bg-white"
                    >
                      {accounts.map(a => (
                        <option key={a.id} value={a.id}>{a.name} ({a.email})</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5 block">Signatur (HTML)</label>
                    <textarea
                      value={editingSignature.signature_html}
                      onChange={e => setEditingSignature({ ...editingSignature, signature_html: e.target.value })}
                      rows={8}
                      className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2.5 focus:border-blue-400 outline-none font-mono"
                      placeholder="<p>Mit freundlichen Grüßen</p><p><strong>Name</strong></p>"
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-gray-600">Standard-Signatur</span>
                      <button
                        onClick={() => setEditingSignature({ ...editingSignature, is_default: !editingSignature.is_default })}
                        className={`w-9 h-5 rounded-full transition-colors relative ${
                          editingSignature.is_default ? 'bg-blue-500' : 'bg-gray-300'
                        }`}
                      >
                        <div className={`w-4 h-4 rounded-full bg-white shadow-sm absolute top-0.5 transition-transform ${
                          editingSignature.is_default ? 'translate-x-4' : 'translate-x-0.5'
                        }`} />
                      </button>
                    </div>
                    <div className="flex items-center gap-2">
                      <button onClick={() => setEditingSignature(null)} className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg">
                        Abbrechen
                      </button>
                      <button onClick={handleSaveSignature} disabled={saving}
                        className="flex items-center gap-2 bg-blue-600 text-white text-sm font-bold px-4 py-2.5 rounded-lg hover:bg-blue-700 disabled:opacity-50">
                        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                        Speichern
                      </button>
                    </div>
                  </div>
                </div>
              ) : (
                <>
                  <button
                    onClick={() => setEditingSignature({
                      account_id: accounts[0]?.id || '',
                      name: 'Neue Signatur',
                      is_default: false,
                      signature_html: '',
                    })}
                    className="w-full flex items-center justify-center gap-2 px-4 py-3 border-2 border-dashed border-gray-300 rounded-xl text-sm font-medium text-gray-500 hover:border-blue-400 hover:text-blue-600 transition-colors"
                  >
                    <Plus className="w-4 h-4" />
                    Neue Signatur erstellen
                  </button>

                  {signatures.length === 0 ? (
                    <div className="text-center py-8">
                      <Pen className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                      <p className="text-sm text-gray-500">Keine Signaturen vorhanden</p>
                      <p className="text-xs text-gray-400 mt-1">Erstelle eine Signatur für deine E-Mails.</p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {signatures.map(sig => {
                        const account = accounts.find(a => a.id === sig.account_id);
                        return (
                          <div
                            key={sig.id}
                            className="flex items-center gap-3 px-4 py-3 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors group cursor-pointer"
                            onClick={() => setEditingSignature(sig)}
                          >
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <p className="text-sm font-medium text-gray-900">{sig.name}</p>
                                {sig.is_default && (
                                  <span className="text-[10px] bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full font-bold">Standard</span>
                                )}
                              </div>
                              <p className="text-xs text-gray-500 mt-0.5">
                                {account?.email || 'Unbekanntes Konto'}
                              </p>
                            </div>
                            <Pen className="w-3.5 h-3.5 text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity" />
                          </div>
                        );
                      })}
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
